import {
  Countries,
  Environment,
  Ethnicities,
  MarketingPricingPlanType,
  ParticipantAudienceType,
  ParticipantType,
  PricingChargeDetailType,
  PricingCreditType,
  ResearchConductType,
  RespondentStatus,
  ResponseStatus,
  TaskParameters,
} from '../../../lib/helpers/enums';
import { censusData } from './quota-census-constants';

/**
 * @description
 * Application configuration that are consistently provided
 * across all environments.
 */
export const APP_CONFIG =
  typeof window !== 'undefined' ? window.APP_CONFIG : {};
export const APP_ENV = APP_CONFIG?.APP_ENV || Environment.Development;
export const APP_DOMAIN = 'respondent.io';

export const API_LEGACY_ENDPOINT = '/dashboard';
export const API_V3_5_ENDPOINT = '/api/v3.5';
export const API_V4_ENDPOINT = '/api/v4';

/**
 * @description
 * Central location for constants used throughout the
 * application and can be accessed via dependency injection.
 */
export const CONSTANTS = {
  Environment,
  ExternalUrl: {
    PricingPlans: 'https://www.respondent.io/pricing',
    Support: 'https://help.respondent.io/en',
    PrivacyPolicy: 'https://www.respondent.io/privacy-policy',
    CustomPlanRequestForm: 'https://get.respondent.io/enterprise-plan-pricing',
  },
  EmailAddress: {
    Sales: 'sales@respondent.io',
    Invoices: 'invoices@respondent.io',
  },
  MarketingPricingPlanType,
  ParticipantAudienceType,
  ParticipantType,
  PricingCreditType,
  ResponseStatus,
  RespondentStatus,
  TaskParameters,
  PricingPlanPeriodTagType: {
    YEARLY: 'yearly',
    MONTHLY: 'monthly',
  },
  PricingPlanComponentType: {
    CREDIT: 'credit',
    CAPABILITY: 'capability',
    CREDIT_RATE: 'creditRate',
  },
  PricingPlanComponentCapability: {
    SSO: 'sso',
    NDA: 'nda',
    PAST_PARTICIPANT: 'pastParticipant',
    PARTICIPANT_DATABASE: 'participantDatabase',
  },
  ProjectRejectionReasonType: {
    INVALID_SCREENER: 'INVALID_SCREENER',
    INAPPROPRIATE_SCREENER: 'INAPPROPRIATE_SCREENER',
    AMBIGUOUS_PROJECT: 'AMBIGUOUS_PROJECT',
    INFORMATION_CONFLICT: 'INFORMATION_CONFLICT',
    SILENT_REJECT: 'SILENT_REJECT',
  },
  ProjectStatus: {
    DRAFT: 'DRAFT',
    // PAID means PUBLISHED
    PAID: 'PAID',
    // TODO proxy for project.closed, but planned
    // @see https://respondent.atlassian.net/browse/XP-2014
    CLOSED: 'CLOSED',
    ARCHIVED: 'ARCHIVED',
    PENDING: 'PENDING',
    REJECTED: 'REJECTED',
  },
  ProjectRecruitingStatus: {
    RECRUITING: 'RECRUITING',
    RECRUITED: 'RECRUITED',
    PAUSED: 'PAUSED',
  },
  ScreenerResponseStatus: {
    DRAFT: 'DRAFT',
    PENDING: 'PENDING',
    INVITED: 'INVITED',
    SCHEDULED: 'SCHEDULED',
    ATTENDED: 'ATTENDED',
    NO_SHOW: 'NOSHOW',
    INVALID_RESPONSE: 'INVALIDRESPONSE',
    REJECTED_QUALITY: 'REJECTEDQUALITY',
    PAID: 'PAID',
    CANCELLED: 'CANCELLED',
  },
  ProviderType: {
    GOOGLE: 'google',
    OUTLOOK: 'outlook',
    FACEBOOK: 'facebook',
    LINKEDIN: 'linkedin',
    ZOOM: 'zoom',
  },
  ResearchConductType,
  /**
   * @description
   * Conversion of raw credits based on
   * participant type.
   */
  CreditConversionRate: {
    [ParticipantType.B2C]: 30,
    [ParticipantType.B2B]: 50,
  },
  /**
   * @description
   * Cost of pausing a plan per month
   * in cents.
   */
  PausePlanCostPerMonth: 5000,
  /**
   * @description
   * Type of balances available.
   */
  BalanceType: {
    Credit: 'credit',
    Incentive: 'incentive',
  },
  MethodologyType: {
    // Unmoderated
    SURVEY: 'survey',
    DIARY_STUDY: 'diaryStudy',
    UNMODERATED_STUDY: 'unmoderatedStudy',
    // Moderated
    ONE_ON_ONE: 'oneOnOne',
    FOCUS_GROUP: 'focusGroup',
    IN_RESPONDENT_HOME: 'inRespondentHome',
    IN_RESPONDENT_OFFICE: 'inRespondentOffice',
  },
  PricingChargeDetailType,
  QuestionType: {
    RADIO: 1,
    MULTI_LINE_TEXTBOX: 2,
    CHECKBOX: 3,
    SINGLE_LINE_TEXTBOX: 4,
    NUMERIC_BOX: 5,
    SLIDER_SCALE: 6,
  },
  Roles: {
    SUPERADMIN: 'superadmin',
    ADMIN: 'admin',
    RESEARCHER: 'researcher',
    RESPONDENT: 'respondent',
    PAYMENT_MANAGER: 'paymentmanager',
    USER_MANAGER: 'usermanager',
    /**
     * @deprecated
     */
    // TODO remove when promotionManager abilities have been completely removed
    PROMOTION_MANAGER: 'promotionManager',
    ADMIN_PRICING: 'adminPricing',
  },
  OrganizationRoles: {
    OWNER: 'owner',
    ADMIN: 'admin',
    MEMBER: 'member',
  },
  TeamRoles: {
    OWNER: 'owner',
    MEMBER: 'member',
    ADMIN: 'admin',
  },
  KindOfResearch: {
    ONE_ON_ONE: 1,
    FOCUS_GROUP: 2,
    /**
     * @deprecated
     */
    MULTI_DAY_STUDY: 3,
    UNMODERATED_STUDY: 4,
    /**
     * @deprecated
     */
    PHONE_INTERVIEW: 5,
    /**
     * @deprecated
     */
    USABILITY_TESTING: 6,
    IN_RESPONDENT_HOME: 8,
    SURVEY: 9,
    DIARY_STUDY: 10,
    IN_RESPONDENT_OFFICE: 11,
  },
  UploadType: {
    NDA: 'nda',
    USER_PIC: 'userPic',
    ATTACHMENT: 'attachment',
  },
  NdaType: {
    NONE: 'none',
    FILE: 'file',
    LINK: 'link',
  },
  TaskType: {
    LOOKBACK: 'lookback',
    SURVEYMONKEY: 'surveymonkey',
    CUSTOM_URL: 'custom_url',
  },
  /**
   * @description
   * Types of trust levels for a user or organization
   */
  TrustLevel: {
    FLAGGED: 0,
    PENDING: 50,
    APPROVED: 95,
    TRUSTED: 100,
  },
  /**
   * @description
   * V4 MasterTransaction types
   */
  MasterTransactionType: {
    /* V4 */
    EXPIRE: 'expire',
    PAYOUT: 'payout',
    PLAN: 'plan',
    PAUSE_PLAN: 'pausePlan',
    UNPAUSE_PLAN: 'unpausePlan',
    PAYG: 'payg',
    TRANSFER_IN: 'transferIn',
    TRANSFER_OUT: 'transferOut',
    RESERVE: 'reserve',
    RELEASE: 'release',
    PROMOTION: 'promotion',
    SUPPORT: 'support',
    CANCEL_CREDIT: 'cancelCredit',
    RESTORE_CREDIT: 'restoreCredit',
    CANCEL_PAYOUT: 'cancelPayout',
    EXTEND_CREDIT: 'extendCredit',
  },
  ProjectKeyQualifier: {
    HOUSEHOLD_INCOME: 'householdIncome',
    PROFESSIONAL_INDUSTRY: 'professionalIndustry',
    PROFESSIONAL_INDUSTRIES: 'professionalIndustries',
    PROFESSIONAL_JOBTITLES: 'professionalJobTitles',
    COMPANY_SIZE: 'companySize',
    SENIORITY: 'seniority',
    TOPICS: 'topics',
    DESIRED_SKILLS: 'desiredSkills',
    RELATIONSHIP_STATUS: 'relationshipStatus',
    LANGUAGES: 'languages',
    NUMBER_OF_CHILDREN: 'numberOfChildren',
    NUMBER_OF_CHILDREN_LIVE_AT_HOME: 'numberOfChildrenLiveAtHome',
    CHILDREN_BIRTH_YEARS: 'childrenBirthYears',
    LIVING_SITUATION: 'livingSituation',
    PETS: 'pets',
    HOME_OWNER: 'homeOwner',
    BUSINESS_OWNER: 'businessOwner',
    INCOME: 'income',
  },
  QuotaCategories: {
    AGE: 'age',
    GENDER: 'gender',
    ETHNICITY: 'ethnicity',
    LOCATION: 'location',
  },
  // Same values as AgeGroup in /lib/@types/core-api/core-api-models.ts
  AgeGroups: [
    { name: '18-20', code: '18-20' },
    { name: '21-29', code: '21-29' },
    { name: '30-39', code: '30-39' },
    { name: '40-54', code: '40-54' },
    { name: '55+', code: '55+' },
    // N/A omitted as it's no longer used
  ],
  // Same values as Gender in /lib/@types/core-api/core-api-models.ts
  Genders: [
    { name: 'Male', code: 'male' },
    { name: 'Female', code: 'female' },
    { name: 'Other', code: 'other' },
    { name: 'Non-binary', code: 'nonbinary' },
    { name: 'Trans-male', code: 'transmale' },
    { name: 'Trans-female', code: 'transfemale' },
    { name: 'Prefer not to answer', code: 'prefernottoanswer' },
    // N/A omitted as it's no longer used
  ],
  Ethnicities,
  Countries,
  CensusData: censusData,
  MaxPayoutCount: 5,
  MaxTipPercentageOfIncentive: 0.25,
  IncreasedMaxTipPercentage: 2,
  ProjectTaskSpecialInstructions: {
    MAX_LENGTH: 2000,
    WARNING_LENGTH: 1900,
  },
  ProjectInvitationSpecialInstructions: {
    MAX_LENGTH: 2000,
    WARNING_LENGTH: 1900,
  },
};

// TODO create routing constants for application-level
export const APP_MODULE_DEFAULT_ROUTE = 'dashboard';
